import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ChannelPicker, CHANNEL_TYPE } from '@/components/ChannelPicker';
import { CustomEmojiPicker } from '@/components/CustomEmojiPicker';
import { DiscordMessagePreview } from '@/components/DiscordMessagePreview';
import { MediaPicker } from '@/components/MediaPicker';
import { api } from '@/lib/api';
import { refreshPostedSoon, type PostedMessage } from '@/lib/posted';
import { PostedList } from '@/components/PostedList';
import { useConfigPage, BackLink } from './_shared';

export function TicketsConfig() {
  const { guildId, settingsQuery, saveMut, currentConfig } = useConfigPage('tickets');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [logChannelId, setLogChannelId] = useState<string | null>(null);

  // Populate form from server state on first load.
  useEffect(() => {
    if (settingsQuery.data) {
      setCategoryId((currentConfig.categoryId as string) ?? null);
      setLogChannelId((currentConfig.logChannelId as string) ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.data]);

  if (settingsQuery.isLoading) return <p className="text-muted-foreground">Lade…</p>;

  return (
    <div className="mx-auto max-w-2xl">
      <BackLink guildId={guildId} />
      <h1 className="mb-6 text-2xl font-semibold">Tickets — Konfiguration</h1>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Ticket-System</CardTitle>
          <CardDescription>
            Wo neue Ticket-Kanäle erstellt werden und wohin Transkripte geschlossener
            Tickets gepostet werden.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Ticket-Kategorie</Label>
            <ChannelPicker
              guildId={guildId}
              value={categoryId}
              onChange={setCategoryId}
              types={[CHANNEL_TYPE.GUILD_CATEGORY]}
              placeholder="— Kategorie wählen —"
            />
          </div>
          <div>
            <Label>Log-Channel (Transkripte)</Label>
            <ChannelPicker
              guildId={guildId}
              value={logChannelId}
              onChange={setLogChannelId}
              types={[CHANNEL_TYPE.GUILD_TEXT]}
            />
          </div>

          <div className="flex items-center justify-end pt-2">
            <Button
              disabled={saveMut.isPending || (!categoryId && !logChannelId)}
              onClick={() =>
                saveMut.mutate({ moduleConfig: { categoryId, logChannelId } })
              }
            >
              {saveMut.isPending ? 'Speichere…' : 'Speichern'}
            </Button>
          </div>
          {saveMut.isSuccess && (
            <p className="text-sm text-success">Gespeichert ✓</p>
          )}
          {saveMut.error && (
            <p className="text-sm text-destructive">
              Fehler: {(saveMut.error as Error).message}
            </p>
          )}
        </CardContent>
      </Card>

      <PostPanelCard guildId={guildId} />
    </div>
  );
}

/**
 * Post the actual button-panel message into a target channel. Split off from
 * the config save because it produces a side-effect (a Discord message),
 * not just a config update.
 */
interface PanelButton {
  label: string;
  emoji: string | null;
}

function PostPanelCard({ guildId }: { guildId: string }) {
  const [targetChannelId, setTargetChannelId] = useState<string | null>(null);
  const [buttons, setButtons] = useState<PanelButton[]>([
    { label: 'help', emoji: null },
    { label: 'issues', emoji: null },
    { label: 'report', emoji: null },
  ]);
  const [panelTitle, setPanelTitle] = useState('');
  const [panelMessage, setPanelMessage] = useState('');
  const [panelImageUrl, setPanelImageUrl] = useState('');
  const [mode, setMode] = useState<'buttons' | 'select'>('buttons');
  const [loaded, setLoaded] = useState(false);
  const qc = useQueryClient();

  /** The form as panel data — posted as-is, and what "Aktualisieren" sends. */
  const panelData = () => ({
    buttons: buttons
      .map((b) => ({ label: b.label.trim(), emoji: b.emoji }))
      .filter((b) => b.label.length > 0),
    panelTitle: panelTitle || undefined,
    panelMessage: panelMessage || undefined,
    panelImageUrl: panelImageUrl || undefined,
    mode,
  });

  const postMut = useMutation({
    mutationFn: () =>
      api('/api/actions/tickets-post-panel', {
        method: 'POST',
        body: JSON.stringify({ guildId, channelId: targetChannelId, ...panelData() }),
      }),
    onSuccess: () => refreshPostedSoon(qc, guildId),
  });

  /** "Laden": an already posted panel back into the form. */
  const load = (p: PostedMessage) => {
    const d = p.data as {
      buttons?: Array<string | { label?: string; emoji?: string | null }>;
      panelTitle?: string;
      panelMessage?: string;
      panelImageUrl?: string;
      mode?: 'buttons' | 'select';
    };
    setButtons(
      (d.buttons ?? []).map((b) =>
        typeof b === 'string' ? { label: b, emoji: null } : { label: b.label ?? '', emoji: b.emoji ?? null }
      )
    );
    setPanelTitle(d.panelTitle ?? '');
    setPanelMessage(d.panelMessage ?? '');
    setPanelImageUrl(d.panelImageUrl ?? '');
    setMode(d.mode === 'select' ? 'select' : 'buttons');
    setLoaded(true);
  };

  const validCount = buttons.filter((b) => b.label.trim().length > 0).length;
  const updateButton = (i: number, patch: Partial<PanelButton>) =>
    setButtons((prev) => prev.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const maxItems = 25;

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-lg">Panel posten</CardTitle>
        <CardDescription>
          Postet die Panel-Nachricht mit den Ticket-Buttons in einen Channel
          deiner Wahl. Pro Button ein optionales Emoji (auch Custom-Emojis von
          anderen Bot-Servern nutzbar).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Ziel-Channel (wo das Panel gepostet wird)</Label>
          <ChannelPicker
            guildId={guildId}
            value={targetChannelId}
            onChange={setTargetChannelId}
            types={[CHANNEL_TYPE.GUILD_TEXT]}
          />
        </div>

        <div>
          <Label>Darstellung</Label>
          <div className="mt-1 flex gap-1">
            {(['buttons', 'select'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={
                  'rounded-md border px-3 py-1.5 text-xs ' +
                  (mode === m
                    ? 'border-primary bg-primary/15 text-primary'
                    : 'bg-background hover:bg-accent')
                }
              >
                {m === 'buttons' ? 'Buttons' : 'Select-Menü (Dropdown)'}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label>Ticket-Kategorien ({validCount}/{maxItems})</Label>
          <div className="mt-1 space-y-2">
            {buttons.map((b, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 rounded-md border p-2">
                <Input
                  value={b.label}
                  onChange={(e) => updateButton(i, { label: e.target.value })}
                  placeholder="Label (z.B. help)"
                  maxLength={80}
                  className="min-w-[160px] flex-1"
                />
                <CustomEmojiPicker
                  value={b.emoji}
                  onChange={(v) => updateButton(i, { emoji: v })}
                  buttonLabel={b.emoji ? undefined : 'Emoji'}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setButtons((prev) => prev.filter((_, j) => j !== i))}
                  disabled={buttons.length === 1}
                  title="Button entfernen"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {buttons.length < maxItems && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setButtons((prev) => [...prev, { label: '', emoji: null }])}
              >
                <Plus className="mr-1 h-3 w-3" /> {mode === 'select' ? 'Option' : 'Button'} hinzufügen
              </Button>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Labels werden lowercase gemacht und als Ticket-Kategorie beim Öffnen gespeichert.
          </p>
        </div>

        <div>
          <Label>Panel-Titel (optional)</Label>
          <Input
            value={panelTitle}
            onChange={(e) => setPanelTitle(e.target.value)}
            placeholder="🎫 Support-Tickets"
          />
        </div>
        <div>
          <Label>Panel-Nachricht (optional)</Label>
          <Textarea emoji
            rows={3}
            value={panelMessage}
            onChange={(e) => setPanelMessage(e.target.value)}
            placeholder="Klicke auf einen Button unten, um ein neues Ticket zu öffnen."
          />
        </div>
        <div>
          <Label>Großes Bild URL (optional)</Label>
          <div className="flex gap-2">
            <Input
              value={panelImageUrl}
              onChange={(e) => setPanelImageUrl(e.target.value)}
              placeholder="https://…"
            />
            <MediaPicker
              guildId={guildId}
              currentUrl={panelImageUrl}
              onSelect={(url) => setPanelImageUrl(window.location.origin + url)}
            />
          </div>
        </div>

        <div className="flex items-center justify-end pt-2">
          <Button
            disabled={
              postMut.isPending || !targetChannelId || validCount === 0 || validCount > maxItems
            }
            onClick={() => postMut.mutate()}
          >
            {postMut.isPending ? 'Poste…' : 'Panel posten'}
          </Button>
        </div>
        {postMut.isSuccess && (
          <p className="text-sm text-success">Panel wurde gepostet ✓</p>
        )}
        {postMut.error && (
          <p className="text-sm text-destructive">
            Fehler: {(postMut.error as Error).message}
          </p>
        )}

        <div className="mt-4 border-t pt-4">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Bereits gepostet
          </div>
          <p className="mb-2 text-xs text-muted-foreground">
            {loaded
              ? 'Panel ins Formular geladen — jetzt oben ändern und beim Post „Aktualisieren“ klicken.'
              : '„Laden“ holt ein gepostetes Panel ins Formular, „Aktualisieren“ überschreibt es mit dem Formular.'}
          </p>
          <PostedList guildId={guildId} type="tickets" getUpdateData={panelData} onLoad={load} />
        </div>

        <div className="mt-4 border-t pt-4">
          <DiscordMessagePreview
            hint="So sieht das Panel im gewählten Channel aus (die Buttons/Select-Optionen werden separat als Discord-Komponenten unter dem Embed angezeigt)."
            embed={{
              title: panelTitle || '🎫 Support-Tickets',
              description:
                panelMessage ||
                'Klicke auf einen Button unten, um ein neues Ticket zu öffnen.',
              imageUrl: panelImageUrl || undefined,
            }}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {buttons
              .filter((b) => b.label.trim())
              .slice(0, mode === 'buttons' ? 25 : 25)
              .map((b, i) =>
                mode === 'buttons' ? (
                  <span
                    key={i}
                    className="rounded-md bg-[#4e5058] px-3 py-1.5 text-xs text-white"
                  >
                    {b.emoji ? `${b.emoji.startsWith('<') || b.emoji.includes(':') ? '📌' : b.emoji} ` : ''}
                    {b.label}
                  </span>
                ) : (
                  <span
                    key={i}
                    className="rounded-md border border-white/20 bg-[#2b2d31] px-2 py-1 text-xs text-white/80"
                  >
                    ▾ {b.label}
                  </span>
                )
              )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
